import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  CloseButton,
  Grid,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconSparkles } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { useSlikaApi } from '../api/useSlikaApi';
import { SlicicaOzadja } from '../nastavitve/UrejanjeSlik';
import { VRSTE_OZADJA, type VrstaOzadja } from '../nastavitve/urejanjeSlik';
import { preteklo, tocke } from '../oblika/oblika';
import { StanjeNalaganja } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { barve } from '../tema';
import { PrejPotem } from './PrejPotem';

type Slika = Shema['SlikaVozila'];
type Zaslon = Shema['UrejanjeSlike'];
type Poskus = Shema['PoskusUrejanja'];

type Izbire = {
  tablica: boolean;
  predmeti: boolean;
  svetloba: boolean;
  ozadje: VrstaOzadja;
  ozadjeId: number | null;
  navodila: string;
};

/**
 * Urejanje slike vozila z umetno inteligenco (okno iz kartice »Slike«): izbire (privzete iz Nastavitve → Urejanje slik)
 * in navodila za to sliko, poskusi (zadnjih 5, primerjava prej/potem; klik na poskus prenese njegove izbire, da jih
 * popravite in poskusite znova) in sprejem, ki dokončno zamenja izvirnik. Vsak poskus se zaračuna v točkah.
 */
export function UrejanjeSlikeAi({
  voziloId,
  slika,
  zapri,
  zamenjana,
}: {
  voziloId: number;
  slika: Slika | null;
  zapri: () => void;
  zamenjana: (stara: Slika, nova: Slika) => void;
}) {
  const telefon = useMediaQuery('(max-width: 48em)');
  const [zaseden, setZaseden] = useState(false);
  return (
    <Modal
      opened={!!slika}
      onClose={() => !zaseden && zapri()}
      title={
        <Group gap={8} wrap="nowrap">
          <IconSparkles size={20} color={barve.primarna} />
          Uredi sliko z umetno inteligenco
          <PomocIkona clanek="urejanje-slik" />
        </Group>
      }
      size={1100}
      radius="lg"
      fullScreen={telefon}
      closeOnClickOutside={false}
    >
      {slika && (
        <Vsebina
          key={slika.id}
          voziloId={voziloId}
          slika={slika}
          zapri={zapri}
          zamenjana={zamenjana}
          zaseden={setZaseden}
        />
      )}
    </Modal>
  );
}

function Vsebina({
  voziloId,
  slika,
  zapri,
  zamenjana,
  zaseden,
}: {
  voziloId: number;
  slika: Slika;
  zapri: () => void;
  zamenjana: (stara: Slika, nova: Slika) => void;
  zaseden: (z: boolean) => void;
}) {
  const kljuc = ['urejanje-slike', voziloId, slika.id];
  const poizvedba = useQuery({
    queryKey: kljuc,
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/vozila/{id}/slike/{slikaId}/urejanje', {
          signal,
          params: { path: { id: voziloId, slikaId: slika.id! } },
        }),
      ),
  });
  if (!poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Urejanja slike ni bilo mogoče naložiti"
      />
    );
  }
  return (
    <Urejanje
      voziloId={voziloId}
      slika={slika}
      zaslon={poizvedba.data}
      kljuc={kljuc}
      zapri={zapri}
      zamenjana={zamenjana}
      zaseden={zaseden}
    />
  );
}

function izPoskusa(p: Poskus): Izbire {
  return {
    tablica: !!p.tablica,
    predmeti: !!p.predmeti,
    svetloba: !!p.svetloba,
    ozadje: (p.ozadje ?? 'NESPREMENJENO') as VrstaOzadja,
    ozadjeId: p.ozadjeId ?? null,
    navodila: p.navodila ?? '',
  };
}

function Urejanje({
  voziloId,
  slika,
  zaslon,
  kljuc,
  zapri,
  zamenjana,
  zaseden,
}: {
  voziloId: number;
  slika: Slika;
  zaslon: Zaslon;
  kljuc: unknown[];
  zapri: () => void;
  zamenjana: (stara: Slika, nova: Slika) => void;
  zaseden: (z: boolean) => void;
}) {
  const odjemalec = useQueryClient();
  const n = zaslon.nastavitve ?? {};
  const poskusi = zaslon.poskusi ?? [];
  const ozadja = n.ozadja ?? [];
  const [izbire, setIzbire] = useState<Izbire>(() =>
    poskusi[0]
      ? izPoskusa(poskusi[0])
      : {
          tablica: !!n.tablica,
          predmeti: !!n.predmeti,
          svetloba: !!n.svetloba,
          ozadje: (n.ozadje ?? 'NESPREMENJENO') as VrstaOzadja,
          ozadjeId: n.ozadjeId ?? null,
          navodila: '',
        },
  );
  const [izbrani, setIzbrani] = useState<number | null>(poskusi[0]?.id ?? null);
  const [dela, setDela] = useState<'uredi' | 'sprejmi' | null>(null);
  const [potrdi, setPotrdi] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const poskus = poskusi.find((p) => p.id === izbrani) ?? null;
  const ozadjeId =
    izbire.ozadjeId != null && ozadja.some((o) => o.id === izbire.ozadjeId) ? izbire.ozadjeId : (ozadja[0]?.id ?? null);
  const nastavi = <K extends keyof Izbire>(k: K, v: Izbire[K]) => setIzbire((x) => ({ ...x, [k]: v }));

  const izvirnik = useSlikaApi(`${slika.id}`, (signal) =>
    api.GET('/api/vozila/{id}/slike/{slikaId}', {
      signal,
      params: { path: { id: voziloId, slikaId: slika.id! }, query: { mala: false } },
      parseAs: 'blob',
    }),
  );
  const urejena = useSlikaApi(izbrani ? `${izbrani}` : null, (signal) =>
    api.GET('/api/vozila/{id}/slike/{slikaId}/poskusi/{poskusId}', {
      signal,
      params: { path: { id: voziloId, slikaId: slika.id!, poskusId: izbrani! }, query: { mala: false } },
      parseAs: 'blob',
    }),
  );

  const zacni = (kaj: 'uredi' | 'sprejmi') => {
    setDela(kaj);
    zaseden(true);
    setNapaka(null);
    setPolja({});
  };
  const koncaj = () => {
    setDela(null);
    zaseden(false);
  };

  const uredi = async () => {
    zacni('uredi');
    try {
      const nov = await podatki(
        api.POST('/api/vozila/{id}/slike/{slikaId}/urejanje', {
          params: { path: { id: voziloId, slikaId: slika.id! } },
          body: { ...izbire, ozadjeId: izbire.ozadje === 'LASTNO' ? (ozadjeId ?? undefined) : undefined },
        }),
      );
      odjemalec.setQueryData(kljuc, nov);
      setIzbrani(nov.poskusi?.[0]?.id ?? null);
      setPotrdi(false);
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka(e.polja.izbire ?? null);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      koncaj();
      await odjemalec.invalidateQueries({ queryKey: ['ai-stanje'] });
    }
  };

  const sprejmi = async () => {
    if (!poskus) {
      return;
    }
    zacni('sprejmi');
    try {
      const nova = await podatki(
        api.POST('/api/vozila/{id}/slike/{slikaId}/poskusi/{poskusId}/sprejmi', {
          params: { path: { id: voziloId, slikaId: slika.id!, poskusId: poskus.id! } },
        }),
      );
      koncaj();
      zamenjana(slika, nova);
      notifications.show({ color: 'green', message: 'Slika je zamenjana z urejeno.' });
      zapri();
    } catch (e) {
      setNapaka(sporocilo(e));
      koncaj();
    }
  };

  const izbrisi = async (p: Poskus) => {
    setNapaka(null);
    try {
      await izvedi(
        api.DELETE('/api/vozila/{id}/slike/{slikaId}/poskusi/{poskusId}', {
          params: { path: { id: voziloId, slikaId: slika.id!, poskusId: p.id! } },
        }),
      );
      const ostali = poskusi.filter((x) => x.id !== p.id);
      odjemalec.setQueryData<Zaslon>(kljuc, { ...zaslon, poskusi: ostali });
      if (izbrani === p.id) {
        setIzbrani(ostali[0]?.id ?? null);
        setPotrdi(false);
      }
    } catch (e) {
      setNapaka(sporocilo(e));
    }
  };

  const izberi = (p: Poskus) => {
    setIzbrani(p.id!);
    setIzbire(izPoskusa(p));
    setPotrdi(false);
  };

  return (
    <Stack gap="md">
      <Grid gap="lg">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="sm">
            <Box pos="relative">
              {izvirnik && urejena && poskus ? (
                <PrejPotem prej={izvirnik} potem={urejena} />
              ) : (
                <Center
                  style={{
                    aspectRatio: `${zaslon.sirina ?? 4} / ${zaslon.visina ?? 3}`,
                    background: barve.izbrano,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  {izvirnik ? (
                    <img
                      src={izvirnik}
                      alt="Izvirnik"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  ) : (
                    <Loader size="sm" />
                  )}
                </Center>
              )}
              {dela === 'uredi' && (
                <Center
                  pos="absolute"
                  inset={0}
                  style={{ background: 'rgba(32, 32, 70, .55)', borderRadius: 8, backdropFilter: 'blur(2px)' }}
                >
                  <Stack align="center" gap={6}>
                    <Loader color="white" />
                    <Text c="white" fw={600}>
                      Urejam sliko …
                    </Text>
                    <Text c="white" size="sm">
                      To traja do pol minute.
                    </Text>
                  </Stack>
                </Center>
              )}
            </Box>
            {poskus ? (
              <Text size="xs" c="dimmed">
                Povlecite črto, da primerjate z izvirnikom. Poskus: {poskus.ustvaril}, {preteklo(poskus.ustvarjen)}
                {poskus.zaracunano != null ? `, ${tocke(poskus.zaracunano)} točke` : ''}.
              </Text>
            ) : (
              <Text size="xs" c="dimmed">
                Izvirnik. Izberite, kaj naj umetna inteligenca uredi, in kliknite »Uredi sliko«.
              </Text>
            )}
            {poskusi.length > 0 && (
              <Stack gap={6}>
                <Text size="sm" fw={600}>
                  Poskusi ({poskusi.length} od 5)
                </Text>
                <Group gap="xs" wrap="nowrap" style={{ overflowX: 'auto', paddingBottom: 4 }}>
                  {poskusi.map((p, i) => (
                    <SlicicaPoskusa
                      key={p.id}
                      voziloId={voziloId}
                      slikaId={slika.id!}
                      poskus={p}
                      st={poskusi.length - i}
                      izbran={p.id === izbrani}
                      onemogoceno={dela !== null}
                      izberi={() => izberi(p)}
                      izbrisi={() => void izbrisi(p)}
                    />
                  ))}
                </Group>
                <Text size="xs" c="dimmed">
                  Klik na poskus prenese njegove izbire in navodila – popravite jih in uredite znova. Poskusi se hranijo
                  7 dni.
                </Text>
              </Stack>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="sm">
            <Checkbox
              label="Registrsko tablico zamenjaj z logotipom"
              description={n.logotip ? undefined : 'Podjetje nima logotipa – tablica bo prazna.'}
              checked={izbire.tablica}
              onChange={(e) => nastavi('tablica', e.currentTarget.checked)}
              disabled={dela !== null}
            />
            <Checkbox
              label="Odstrani moteče predmete"
              checked={izbire.predmeti}
              onChange={(e) => nastavi('predmeti', e.currentTarget.checked)}
              disabled={dela !== null}
            />
            <Checkbox
              label="Izboljšaj svetlobo"
              checked={izbire.svetloba}
              onChange={(e) => nastavi('svetloba', e.currentTarget.checked)}
              disabled={dela !== null}
            />
            <Stack gap={6}>
              <Text size="sm" fw={500}>
                Ozadje
              </Text>
              <SegmentedControl
                data={VRSTE_OZADJA}
                value={izbire.ozadje}
                onChange={(v) => nastavi('ozadje', v as VrstaOzadja)}
                disabled={dela !== null}
                fullWidth
                size="sm"
              />
              {izbire.ozadje === 'LASTNO' &&
                (ozadja.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    Nimate še lastnih ozadij.{' '}
                    <Anchor component={Link} to="/nastavitve/urejanje-slik" size="xs">
                      Dodajte fotografijo salona
                    </Anchor>
                  </Text>
                ) : (
                  <SimpleGrid cols={3} spacing={6}>
                    {ozadja.map((o) => (
                      <Tooltip key={o.id} label={o.naziv} openDelay={300}>
                        <UnstyledButton
                          onClick={() => nastavi('ozadjeId', o.id!)}
                          aria-label={`Ozadje ${o.naziv}`}
                          aria-pressed={ozadjeId === o.id}
                          disabled={dela !== null}
                          style={{
                            borderRadius: 10,
                            padding: 2,
                            outline: ozadjeId === o.id ? `3px solid ${barve.primarna}` : `1px solid ${barve.obroba}`,
                          }}
                        >
                          <SlicicaOzadja ozadje={o} />
                        </UnstyledButton>
                      </Tooltip>
                    ))}
                  </SimpleGrid>
                ))}
              {(polja.ozadje || polja.ozadjeId) && (
                <Text size="xs" c="red">
                  {polja.ozadje ?? polja.ozadjeId}
                </Text>
              )}
            </Stack>
            <Textarea
              size="md"
              label="Navodila za to sliko (neobvezno)"
              placeholder="npr. odstrani senco fotografa, poravnaj obzorje"
              value={izbire.navodila}
              onChange={(e) => nastavi('navodila', e.currentTarget.value)}
              error={polja.navodila}
              disabled={dela !== null}
              autosize
              minRows={2}
              maxRows={6}
            />
            <Text size="xs" c="dimmed">
              Vozilo ostane nespremenjeno. Upoštevajo se tudi{' '}
              <Anchor component={Link} to="/nastavitve/urejanje-slik" size="xs">
                navodila podjetja
              </Anchor>
              {n.navodila ? '' : ' (niso vpisana)'}.
            </Text>
            {zaslon.razlog && (
              <Alert color="yellow" variant="light" role="alert">
                {zaslon.razlog}
              </Alert>
            )}
            {napaka && (
              <Alert color="red" variant="light" role="alert">
                {napaka}
              </Alert>
            )}
            <Button
              leftSection={<IconSparkles size={18} />}
              onClick={() => void uredi()}
              loading={dela === 'uredi'}
              disabled={!!zaslon.razlog || (dela !== null && dela !== 'uredi')}
              fullWidth
            >
              {poskusi.length > 0 ? 'Uredi znova' : 'Uredi sliko'}
            </Button>
            <Text size="xs" c="dimmed" ta="center">
              {zaslon.cena != null
                ? `Ena obdelava stane približno ${zaslon.cena < 0.01 ? 'manj kot 0,01' : tocke(Math.round(zaslon.cena * 100) / 100)} točke. `
                : ''}
              Stanje: {tocke(zaslon.tocke)} točke.
            </Text>
          </Stack>
        </Grid.Col>
      </Grid>

      {potrdi && poskus ? (
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={20} />}
          title="Zamenjam izvirnik z urejeno sliko?"
        >
          <Stack gap="sm">
            <Text size="sm">
              Urejena slika bo dokončno zamenjala izvirnik na istem mestu (tudi na javni strani). Izvirnika potem ne bo
              več mogoče povrniti, drugi poskusi te slike se izbrišejo.
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => setPotrdi(false)} disabled={dela !== null}>
                Prekliči
              </Button>
              <Button
                color="orange"
                leftSection={<IconCheck size={18} />}
                onClick={() => void sprejmi()}
                loading={dela === 'sprejmi'}
              >
                Zamenjaj sliko
              </Button>
            </Group>
          </Stack>
        </Alert>
      ) : (
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela !== null}>
            Zapri
          </Button>
          <Button
            leftSection={<IconCheck size={18} />}
            onClick={() => setPotrdi(true)}
            disabled={!poskus || dela !== null}
          >
            Sprejmi izbrani poskus
          </Button>
        </Group>
      )}
    </Stack>
  );
}

/** Sličica poskusa z zaporedno številko in brisanjem. */
function SlicicaPoskusa({
  voziloId,
  slikaId,
  poskus,
  st,
  izbran,
  onemogoceno,
  izberi,
  izbrisi,
}: {
  voziloId: number;
  slikaId: number;
  poskus: Poskus;
  st: number;
  izbran: boolean;
  onemogoceno: boolean;
  izberi: () => void;
  izbrisi: () => void;
}) {
  const url = useSlikaApi(`${poskus.id}`, (signal) =>
    api.GET('/api/vozila/{id}/slike/{slikaId}/poskusi/{poskusId}', {
      signal,
      params: { path: { id: voziloId, slikaId, poskusId: poskus.id! }, query: { mala: true } },
      parseAs: 'blob',
    }),
  );
  return (
    <Box pos="relative" style={{ flexShrink: 0 }}>
      <UnstyledButton
        onClick={izberi}
        disabled={onemogoceno}
        aria-label={`Poskus ${st}`}
        aria-pressed={izbran}
        style={{
          display: 'block',
          width: 112,
          borderRadius: 10,
          padding: 2,
          outline: izbran ? `3px solid ${barve.primarna}` : `1px solid ${barve.obroba}`,
        }}
      >
        <Center style={{ aspectRatio: '4 / 3', background: barve.izbrano, borderRadius: 8, overflow: 'hidden' }}>
          {url ? (
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Loader size="xs" />
          )}
        </Center>
      </UnstyledButton>
      <Badge size="xs" variant="filled" color="dark" style={{ position: 'absolute', left: 6, top: 6, opacity: 0.85 }}>
        {st}
      </Badge>
      <Tooltip label="Izbriši poskus">
        <CloseButton
          size="sm"
          radius="xl"
          variant="filled"
          aria-label={`Izbriši poskus ${st}`}
          onClick={izbrisi}
          disabled={onemogoceno}
          style={{ position: 'absolute', right: 4, top: 4, background: 'rgba(255,255,255,.9)' }}
        />
      </Tooltip>
    </Box>
  );
}

/** Gumb »Uredi z AI« pri sliki vozila. */
export function GumbUrejanjaAi({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip label="Uredi z umetno inteligenco">
      <ActionIcon variant="subtle" size="md" onClick={onClick} aria-label="Uredi z umetno inteligenco">
        <IconSparkles size={17} />
      </ActionIcon>
    </Tooltip>
  );
}
